import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Button,
  Flex,
  Text,
  HStack,
  VStack,
  Box,
  Icon,
  Table,
  Tbody,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  Alert,
  AlertIcon,
  Select,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Divider,
  Progress
} from "@chakra-ui/react";
import { FiCreditCard, FiPlus, FiChevronDown } from "react-icons/fi";
import { FaChevronLeft, FaChevronRight, FaSync } from "react-icons/fa";
import UserProfilePaymentTableRow from "components/Tables/UserProfilePaymentTableRow";
import ModalOrderEdit from "components/Modal/ModalOrderEdit";
import { triggerStatusChange } from "services/billing";
import { deleteOrder, modifyOrder } from "services/woo";
import { validateAdminCompleteAction, getAdminYearViewInfo } from "services/validation";
import { createLogger } from "utils/logger";

const Payments = ({ customerData, ordersData, onOrderClick, onNewOrder, onRefresh, isLoading = false }) => {
  // Logger optimizado (memoizado para evitar re-creación)
  const logger = useMemo(() => createLogger('Payments'), []);
  logger.log('🔄 Component rendered with isLoading:', isLoading);

  const isMobile = useBreakpointValue({ base: true, md: false });
  const toast = useToast();
  
  // Estado del modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOrder, setModalOrder] = useState({
    id: '',
    meta_data: [],
    billing: { first_name: '', last_name: '', email: '' },
    total: '',
    status: '',
  });
  const [modalCustomer, setModalCustomer] = useState({
    first_name: '',
    last_name: '',
  });
  
  // Estados para selección múltiple
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  
  // Estados para progreso en tiempo real
  const [progressData, setProgressData] = useState({
    current: 0,
    total: 0,
    currentOrder: null,
    completed: [],
    errors: []
  });
  
  // Estado para información de validación de año
  const [yearViewInfo, setYearViewInfo] = useState({ shouldShow: false });
  
  // LocalStorage keys
  const STORAGE_KEYS = {
    ITEMS_PER_PAGE: 'payments-items-per-page',
    SELECTED_YEAR: 'payments-selected-year',
    CURRENT_TAB: 'profile-current-tab'
  };

  // Función para cargar configuración desde localStorage
  const loadFromStorage = (key, defaultValue) => {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // Función para guardar en localStorage
  const saveToStorage = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn('Error saving to localStorage:', error);
    }
  };

  // Paginación simple
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => loadFromStorage(STORAGE_KEYS.ITEMS_PER_PAGE, 50));
  
  // Filtro de año
  const [selectedYear, setSelectedYear] = useState(() => loadFromStorage(STORAGE_KEYS.SELECTED_YEAR, new Date().getFullYear()));
  
  // Función de ordenamiento por campo 'mes' (YYYY-MM), igual que el template del cliente
  const sortOrdersByBusinessLogic = (orders) => {
    if (!orders || !Array.isArray(orders)) return [];

    return [...orders].sort((a, b) => {
      const aMes = a.meta_data?.find(m => m.key === 'mes')?.value || '';
      const bMes = b.meta_data?.find(m => m.key === 'mes')?.value || '';

      // 1. Ordenar por campo mes ASC (YYYY-MM → orden cronológico natural)
      if (aMes && bMes) {
        const cmp = aMes.localeCompare(bMes);
        if (cmp !== 0) return cmp;
      } else if (aMes) {
        return -1; // órdenes con mes van antes
      } else if (bMes) {
        return 1;
      }

      // 2. Fallback: fecha de creación ASC
      return new Date(a.date_created) - new Date(b.date_created);
    });
  };

  // Función para filtrar órdenes por año de mensualidad (campo 'mes')
  const filterOrdersByYear = (orders, year) => {
    if (!orders || !Array.isArray(orders)) return [];
    
    return orders.filter(order => {
      // Buscar el campo 'mes' en meta_data
      const mesValue = order.meta_data?.find(meta => meta.key === 'mes')?.value;
      if (!mesValue) return false;
      
      // Extraer año del campo mes (formato: YYYY-MM o YYYY-MM-DD)
      const mesYear = parseInt(mesValue.split('-')[0]);
      return mesYear === year;
    });
  };

  // Función memoizada para obtener años disponibles (optimizada)
  const availableYears = useMemo(() => {
    if (!ordersData || !Array.isArray(ordersData)) return [new Date().getFullYear(), 2026];
    
    // Extraer años de los campos 'mes' de las órdenes
    const years = ordersData.map(order => {
      const mesValue = order.meta_data?.find(meta => meta.key === 'mes')?.value;
      return mesValue ? parseInt(mesValue.split('-')[0]) : null;
    }).filter(Boolean); // Filtrar valores nulos
    
    const uniqueYears = [...new Set(years)].sort((a, b) => b - a); // Más recientes primero
    
    // Siempre incluir 2026 para admins
    if (!uniqueYears.includes(2026)) {
      uniqueYears.unshift(2026); // Agregar al inicio (más reciente)
    }
    
    return uniqueYears.length > 0 ? uniqueYears : [new Date().getFullYear(), 2026];
  }, [ordersData]);

  // Aplicar filtro de año y ordenamiento (memoizado)
  const { filteredOrders, sortedOrders, totalItems, totalPages, currentItems, startIndex, endIndex } = useMemo(() => {
    const filtered = filterOrdersByYear(ordersData, selectedYear);
    const sorted = sortOrdersByBusinessLogic(filtered);
    const total = sorted ? sorted.length : 0;
    const pages = Math.ceil(total / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    const current = sorted ? sorted.slice(start, end) : [];

    return {
      filteredOrders: filtered,
      sortedOrders: sorted,
      totalItems: total,
      totalPages: pages,
      currentItems: current,
      startIndex: start,
      endIndex: end
    };
  }, [ordersData, selectedYear, currentPage, itemsPerPage]);

  // Manejar cambio de año (memoizado)
  const handleYearChange = useCallback((newYear) => {
    const yearValue = parseInt(newYear);
    setSelectedYear(yearValue);
    saveToStorage(STORAGE_KEYS.SELECTED_YEAR, yearValue);
    setCurrentPage(1); // Reset a primera página
    setSelectedOrders(new Set()); // Reset selección
    setSelectAll(false);
  }, []);

  // Evitar loop infinito - usar ref para tracking
  const ordersRef = useRef(ordersData);
  const yearRef = useRef(selectedYear);
  
  useEffect(() => {
    // Solo ejecutar si realmente cambió
    if (ordersRef.current !== ordersData || yearRef.current !== selectedYear) {
      logger.log('Orders data updated:', ordersData?.length || 0, 'orders');
      ordersRef.current = ordersData;
      yearRef.current = selectedYear;
      
      // Reset selección cuando cambien las órdenes (solo si hay selección activa)
      setSelectedOrders(prev => prev.size > 0 ? new Set() : prev);
      setSelectAll(prev => prev ? false : prev);
    }
  }, [ordersData, selectedYear]);
  
  // Efecto para validar información de año
  useEffect(() => {
    const checkYearValidation = async () => {
      if (customerData?.id && selectedYear) {
        try {
          const info = await getAdminYearViewInfo(customerData.id, selectedYear);
          setYearViewInfo(info);
        } catch (error) {
          logger.error('Error obteniendo validación de año:', error);
          setYearViewInfo({ shouldShow: false });
        }
      }
    };
    
    checkYearValidation();
  }, [customerData?.id, selectedYear]);

  // Funciones de selección múltiple
  const handleSelectOrder = (orderId, isChecked) => {
    const newSelected = new Set(selectedOrders);
    if (isChecked) {
      newSelected.add(orderId);
    } else {
      newSelected.delete(orderId);
    }
    setSelectedOrders(newSelected);
    
    // Actualizar estado de "seleccionar todo"
    setSelectAll(newSelected.size === currentItems.length);
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allIds = new Set(currentItems.map(order => order.id));
      setSelectedOrders(allIds);
      setSelectAll(true);
    } else {
      setSelectedOrders(new Set());
      setSelectAll(false);
    }
  };

  // Acciones en lote usando servicios reales WooCommerce
  const handleBulkAction = async (action) => {
    if (selectedOrders.size === 0) {
      toast({
        title: "Sin selección",
        description: "Debe seleccionar al menos una orden",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const selectedOrdersList = Array.from(selectedOrders);
    
    // Ordenar las órdenes seleccionadas por la misma lógica de negocio
    // (más antigua primero) para procesar en el orden correcto
    const selectedOrdersData = selectedOrdersList.map(id => 
      sortedOrders.find(order => order.id === id)
    ).filter(Boolean);
    
    const sortedSelectedOrders = sortOrdersByBusinessLogic(selectedOrdersData);
    const sortedSelectedOrderIds = sortedSelectedOrders.map(order => order.id);
    
    const actionLabel = {
      'completed':  'completar',
      'pending':    'marcar como pendiente',
      'cancelled':  'cancelar',
      'repactado':  'marcar como repactado',
      'trash':      'eliminar'
    }[action] || action;

    // Validación especial para acción 'completed' con año futuro
    if (action === 'completed' && customerData?.id) {
      try {
        const validation = await validateAdminCompleteAction(customerData.id, selectedYear);
        
        if (!validation.canProceed) {
          toast({
            title: "Acción bloqueada",
            description: validation.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        
        if (validation.requiresConfirmation) {
          const shouldProceedWithWarning = window.confirm(validation.message);
          if (!shouldProceedWithWarning) return;
        }
      } catch (error) {
        console.error('Error en validación:', error);
        toast({
          title: "Error de validación",
          description: "No se pudo validar la acción",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    const shouldProceed = window.confirm(
      `¿Está seguro que desea ${actionLabel} ${selectedOrdersList.length} orden${selectedOrdersList.length !== 1 ? 'es' : ''}?\n\nEsta acción se procesará una por una para garantizar que se completen todos los hooks y procesos automáticos.`
    );

    if (!shouldProceed) return;

    setIsProcessingAction(true);
    setProgressData({
      current: 0,
      total: sortedSelectedOrderIds.length,
      currentOrder: null,
      completed: [],
      errors: []
    });
    
    try {
      toast({
        title: "Iniciando procesamiento...",
        description: `Procesando ${sortedSelectedOrderIds.length} órdenes una por una (de más antigua a más nueva)`,
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      const completed = [];
      const errors = [];

      // Procesar cada orden individualmente EN ORDEN CRONOLÓGICO
      for (let i = 0; i < sortedSelectedOrderIds.length; i++) {
        const orderId = sortedSelectedOrderIds[i];
        const currentOrder = sortedSelectedOrders.find(order => order.id === orderId);
        
        // Actualizar progreso
        setProgressData({
          current: i + 1,
          total: sortedSelectedOrderIds.length,
          currentOrder: currentOrder,
          completed: [...completed],
          errors: [...errors]
        });

        try {
          console.log(`🔄 Procesando orden ${i + 1}/${sortedSelectedOrderIds.length}: #${orderId} → ${action}`);
          
          // Obtener fecha de vencimiento para logging
          const fechaVencimiento = currentOrder?.meta_data?.find(m => m.key === 'fecha_vencimiento')?.value;
          const mesInfo = currentOrder?.meta_data?.find(m => m.key === 'mes')?.value;
          
          console.log(`📅 Orden #${orderId}: ${mesInfo} (vence: ${fechaVencimiento})`);
          
          let result;
          
          if (action === 'trash') {
            // Usar deleteOrder para eliminar
            result = await deleteOrder(orderId);
          } else {
            // Usar triggerStatusChange para cambios de estado
            result = await triggerStatusChange(orderId, action);
          }

          if (result && result.success !== false) {
            completed.push({
              orderId,
              order: currentOrder,
              result: result
            });
            
            console.log(`✅ Orden #${orderId} (${mesInfo}) procesada exitosamente`);
          } else {
            throw new Error(result?.message || 'Error desconocido');
          }

          // Delay pequeño entre órdenes para evitar saturar el servidor
          if (i < sortedSelectedOrderIds.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }

        } catch (error) {
          console.error(`❌ Error procesando orden #${orderId}:`, error);
          errors.push({
            orderId,
            order: currentOrder,
            error: error.message || 'Error desconocido'
          });
        }
      }

      // Actualizar progreso final
      setProgressData({
        current: sortedSelectedOrderIds.length,
        total: sortedSelectedOrderIds.length,
        currentOrder: null,
        completed,
        errors
      });

      // Mostrar resultado final
      const successCount = completed.length;
      const errorCount = errors.length;

      if (errorCount === 0) {
        toast({
          title: "✅ Procesamiento completado",
          description: `${successCount} orden${successCount !== 1 ? 'es' : ''} ${actionLabel}${successCount !== 1 ? 's' : ''} exitosamente`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (successCount === 0) {
        toast({
          title: "❌ Procesamiento fallido",
          description: `No se pudo procesar ninguna orden. ${errorCount} errores.`,
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: "⚠️ Procesamiento parcial",
          description: `${successCount} éxito${successCount !== 1 ? 's' : ''}, ${errorCount} error${errorCount !== 1 ? 'es' : ''}`,
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      }

      // Reset selección
      setSelectedOrders(new Set());
      setSelectAll(false);

      // Refrescar datos para ver los cambios
      if (onRefresh) {
        onRefresh();
      }

    } catch (error) {
      console.error('Error general en procesamiento en lote:', error);
      toast({
        title: "Error crítico",
        description: "Error inesperado durante el procesamiento",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessingAction(false);
      // Reset progreso después de un delay
      setTimeout(() => {
        setProgressData({
          current: 0,
          total: 0,
          currentOrder: null,
          completed: [],
          errors: []
        });
      }, 3000);
    }
  };

  const handleOrderClick = (order, event) => {
    // No abrir modal si se hizo click en el checkbox
    if (event && event.target && event.target.type === 'checkbox') {
      return;
    }
    // Si el padre maneja el modal, delegar: abrir ambos genera doble instancia
    if (onOrderClick) {
      onOrderClick(order);
      return;
    }
    setModalOrder(order);
    setModalCustomer({
      first_name: customerData?.first_name || '',
      last_name: customerData?.last_name || '',
    });
    setIsModalOpen(true);
  };
  
  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  // Manejar cambio de elementos por página
  const handlePerPageChange = (newPerPage) => {
    const perPageValue = parseInt(newPerPage);
    setItemsPerPage(perPageValue);
    saveToStorage(STORAGE_KEYS.ITEMS_PER_PAGE, perPageValue);
    setCurrentPage(1);
  };
  
  // Captions para la tabla
  const captions = isMobile ? [] : ['Estado', 'Orden', 'Mes/Año', 'Creación', 'Actualización', 'Monto', 'Comprobantes'];

  // Componente de progreso en tiempo real
  const ProgressIndicator = () => {
    if (!isProcessingAction || progressData.total === 0) return null;

    const percentage = Math.round((progressData.current / progressData.total) * 100);

    return (
      <Box mb={4} p={4} bg="coral.tint" borderRadius="16px" border="1px solid" borderColor="coral.soft" fontFamily="jakarta">
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Text fontSize="13.5px" fontWeight="800" color="coral.700">
              Procesando acciones en lote
            </Text>
            <Text fontSize="12.5px" color="coral.600" fontWeight="600">
              {progressData.current} / {progressData.total} ({percentage}%)
            </Text>
          </HStack>

          <Progress
            value={percentage}
            size="sm"
            hasStripe
            isAnimated
            borderRadius="full"
            sx={{ '& > div': { background: '#EF5A2A' } }}
          />

          {progressData.currentOrder && (
            <Text fontSize="11.5px" color="coral.700">
              Procesando orden #{progressData.currentOrder.id}...
            </Text>
          )}

          {progressData.completed.length > 0 && (
            <HStack spacing={4} fontSize="11.5px">
              <Text color="mint.ink" fontWeight="700">
                Exitosos: {progressData.completed.length}
              </Text>
              {progressData.errors.length > 0 && (
                <Text color="rose.ink" fontWeight="700">
                  Errores: {progressData.errors.length}
                </Text>
              )}
            </HStack>
          )}
        </VStack>
      </Box>
    );
  };

  // Acciones móviles
  const MobileBulkActions = () => {
    if (selectedOrders.size === 0 || !isMobile) return null;
    
    return (
      <Box mb={4} p={3} bg="cream.bg2" borderRadius="14px" border="1px solid" borderColor="cream.line" fontFamily="jakarta">
        <HStack justify="space-between" mb={2}>
          <Text fontSize="13px" fontWeight="800" color="ink.900">
            {selectedOrders.size} seleccionada{selectedOrders.size !== 1 ? 's' : ''}
          </Text>
          <Button size="xs" variant="ghost" color="ink.400" onClick={() => {setSelectedOrders(new Set()); setSelectAll(false);}}>
            Limpiar
          </Button>
        </HStack>
        <HStack spacing={2} flexWrap="wrap">
          <Button
            size="xs" borderRadius="8px" fontWeight="700" bg="mint.bg" color="mint.ink"
            _hover={{ bg: 'mint.bg' }}
            onClick={() => handleBulkAction('completed')}
            isDisabled={isProcessingAction}
          >
            Completar
          </Button>
          <Button
            size="xs" borderRadius="8px" fontWeight="700" bg="butter.bg" color="butter.ink"
            _hover={{ bg: 'butter.bg' }}
            onClick={() => handleBulkAction('pending')}
            isDisabled={isProcessingAction}
          >
            Pendiente
          </Button>
          <Button
            size="xs" borderRadius="8px" fontWeight="700" bg="teal.bg" color="teal.ink"
            _hover={{ bg: 'teal.bg' }}
            onClick={() => handleBulkAction('repactado')}
            isDisabled={isProcessingAction}
          >
            Repactar
          </Button>
          <Button
            size="xs" borderRadius="8px" fontWeight="700"
            border="1.5px solid" borderColor="rose.ink" bg="white" color="rose.ink"
            _hover={{ bg: 'rose.bg' }}
            onClick={() => handleBulkAction('cancelled')}
            isDisabled={isProcessingAction}
          >
            Cancelar
          </Button>
        </HStack>
      </Box>
    );
  };

  // Componente de información de validación de año
  const YearValidationInfo = () => {
    if (!yearViewInfo.shouldShow) return null;

    return (
      <Alert status={yearViewInfo.type || "info"} borderRadius="lg" mb={4}>
        <AlertIcon />
        <Box flex="1">
          <Text fontSize="sm" lineHeight="1.4">
            {yearViewInfo.message}
          </Text>
        </Box>
      </Alert>
    );
  };

  return (
    <Box fontFamily="jakarta">
      {!onOrderClick && (
        <ModalOrderEdit
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          order={modalOrder}
          action={'view'}
          customer={modalCustomer}
        />
      )}

      {/* Indicador de progreso - Fuera de la card */}
      <ProgressIndicator />

      {/* Información de validación de año */}
      <YearValidationInfo />

      <Box
        bg="white" border="1px solid" borderColor="cream.line" borderRadius="20px"
        p={{ base: 4, md: 6 }} my={4} boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
      >
        <Flex
          justify="space-between"
          align={isMobile ? "flex-start" : "center"}
          flexWrap="wrap"
          gap={4}
          direction={isMobile ? "column" : "row"}
          w="100%"
          mb={4}
        >
          <Box>
            <HStack spacing={2} mb={1}>
              <Flex w="34px" h="34px" borderRadius="10px" bg="coral.soft" align="center" justify="center">
                <Icon as={FiCreditCard} color="coral.700" boxSize={4} />
              </Flex>
              <Text fontSize="15px" color="ink.900" fontWeight="800">
                Historial de pagos
              </Text>
            </HStack>
            {ordersData && ordersData.length > 0 && (
              <VStack align="flex-start" spacing={2} w="100%">
                <HStack spacing={3} flexWrap="wrap" w="100%">
                  <Text fontSize="12.5px" color="ink.400" fontWeight="500">
                    {totalItems} pago{totalItems !== 1 ? 's' : ''} en {selectedYear}
                    {selectedOrders.size > 0 && !isMobile && (
                      <Box as="span" ml={2} px="9px" py="2px" borderRadius="999px" bg="coral.soft" color="coral.700"
                        fontSize="11px" fontWeight="700">
                        {selectedOrders.size} seleccionada{selectedOrders.size !== 1 ? 's' : ''}
                      </Box>
                    )}
                  </Text>

                  {/* Selector de año - más espacio en móvil */}
                  <HStack spacing={2}>
                    <Text fontSize="11.5px" color="ink.400" fontWeight="600">Año:</Text>
                    <Select
                      size="xs"
                      value={selectedYear}
                      onChange={(e) => handleYearChange(e.target.value)}
                      width={isMobile ? "70px" : "80px"}
                      borderRadius="8px" fontWeight="700" color="ink.700"
                      border="1.5px solid" borderColor="cream.line2" bg="white"
                      _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
                    >
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </Select>
                  </HStack>
                </HStack>

                {/* Badge de selección en móvil - línea separada */}
                {selectedOrders.size > 0 && isMobile && (
                  <Box as="span" px="9px" py="2px" borderRadius="999px" bg="coral.soft" color="coral.700"
                    fontSize="11px" fontWeight="700">
                    {selectedOrders.size} seleccionada{selectedOrders.size !== 1 ? 's' : ''}
                  </Box>
                )}
              </VStack>
            )}
          </Box>

          <HStack spacing={2}>
            {/* Acciones en lote - Solo mostrar si hay selección */}
            {selectedOrders.size > 0 && !isMobile && (
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm" borderRadius="9px" fontWeight="700" fontSize="12.5px"
                  border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
                  _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
                  rightIcon={<Icon as={FiChevronDown} />}
                  isLoading={isProcessingAction}
                  loadingText="Procesando"
                >
                  Acciones ({selectedOrders.size})
                </MenuButton>
                <MenuList borderRadius="12px" fontFamily="jakarta">
                  <MenuItem onClick={() => handleBulkAction('completed')} fontSize="13.5px">
                    Marcar como Completado
                  </MenuItem>
                  <MenuItem onClick={() => handleBulkAction('pending')} fontSize="13.5px">
                    Marcar como Pendiente
                  </MenuItem>
                  <MenuItem onClick={() => handleBulkAction('repactado')} fontSize="13.5px">
                    Marcar como Repactado
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={() => handleBulkAction('cancelled')}
                    color="#DC2626" fontSize="13.5px"
                    _hover={{ bg: "rose.bg" }}
                  >
                    Cancelar Órdenes
                  </MenuItem>
                </MenuList>
              </Menu>
            )}

            {/* Botón de actualizar */}
            <Button
              leftIcon={<Icon as={FaSync} />}
              size="sm" borderRadius="9px" fontWeight="700" fontSize="12.5px"
              border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
              _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
              onClick={() => { if (onRefresh) onRefresh(); }}
              isLoading={isLoading}
              loadingText="Actualizando..."
            >
              Actualizar
            </Button>

            <Button
              size="sm" borderRadius="9px" fontWeight="700" fontSize="12.5px"
              bg="coral.500" color="#fff"
              _hover={{ bg: 'coral.600' }}
              leftIcon={<FiPlus />}
              onClick={onNewOrder}
            >
              Nuevo cobro
            </Button>
          </HStack>
        </Flex>

        {/* Acciones móviles */}
        <MobileBulkActions />

        {currentItems && currentItems.length > 0 ? (
          <Box
            position="relative"
            minHeight="200px"
            w="100%"
            overflowX={isMobile ? "hidden" : "auto"}
            className={!isMobile ? "horizontal-scroll" : ""}
            opacity={isLoading ? 0.6 : 1}
            transition="opacity 0.2s"
            border="1px solid" borderColor="cream.line" borderRadius="14px"
          >
            <Table variant='simple' size="sm" w="100%">
              {!isMobile && (
                <Thead>
                  <Tr height="40px" bg="cream.bg2">
                    <Th w="40px" py={2}>
                      <Checkbox
                        isChecked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        colorScheme="orange"
                        size="sm"
                      />
                    </Th>
                    {captions.map((caption, idx) => (
                      <Th color='ink.400' key={idx} fontSize="11px" py={2}>
                        {caption}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
              )}
              <Tbody>
                {currentItems.map((row) => (
                  <UserProfilePaymentTableRow
                    key={row.id}
                    {...row}
                    onClick={(event) => handleOrderClick(row, event)}
                    isSelected={selectedOrders.has(row.id)}
                    onSelect={(isChecked) => handleSelectOrder(row.id, isChecked)}
                    showCheckbox={true}
                  />
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Box
            p={8} bg="cream.bg2" borderRadius="16px" border="1.5px dashed" borderColor="cream.line2"
            textAlign="center"
          >
            <Icon as={FiCreditCard} boxSize={9} color="ink.400" mb={3} />
            <Text fontSize="13.5px" color="ink.700" fontWeight="700">
              No hay pagos en {selectedYear}
            </Text>
            <Text fontSize="12.5px" color="ink.400" mt={1}>
              {availableYears.length > 1 ?
                'Selecciona otro año para ver más pagos' :
                'Los pagos y transacciones aparecerán aquí'
              }
            </Text>
            {availableYears.length > 1 && !availableYears.includes(selectedYear) && (
              <Text fontSize="11.5px" color="ink.400" mt={2}>
                Años disponibles: {availableYears.join(', ')}
              </Text>
            )}
          </Box>
        )}

        {/* Paginación */}
        {currentItems && currentItems.length > 0 && totalPages > 1 && (
          <Box pt={4}>
            <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={3}>
              <HStack spacing={2}>
                <Text fontSize="12.5px" color="ink.400" fontWeight="600">Mostrar:</Text>
                <Select
                  size="sm"
                  value={itemsPerPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                  width="auto" borderRadius="9px" fontWeight="700" fontSize="12.5px"
                  border="1.5px solid" borderColor="cream.line2" color="ink.700"
                  _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Select>
                <Text fontSize="12.5px" color="ink.400" fontWeight="600">resultados</Text>
              </HStack>

              <Text fontSize="12.5px" color="ink.400" fontWeight="600">
                Mostrando {startIndex + 1} - {Math.min(endIndex, totalItems)} de {totalItems}
              </Text>
            </Flex>

            <Flex justify="center" align="center">
              <HStack spacing={2}>
                <Button
                  size="sm" borderRadius="9px" fontWeight="700" fontSize="12.5px"
                  border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
                  _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
                  leftIcon={<Icon as={FaChevronLeft} boxSize="11px" />}
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage <= 1}
                >
                  Anterior
                </Button>

                <Text fontSize="12.5px" color="ink.700" px={3} fontWeight="700">
                  Página {currentPage} de {totalPages}
                </Text>

                <Button
                  size="sm" borderRadius="9px" fontWeight="700" fontSize="12.5px"
                  border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
                  _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
                  rightIcon={<Icon as={FaChevronRight} boxSize="11px" />}
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage >= totalPages}
                >
                  Siguiente
                </Button>
              </HStack>
            </Flex>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Payments;
